import { useState } from 'nuxt/app';
import { computed, watch } from 'vue';
import { useClientOnboarding } from './useClientOnboarding';
import { useAuthApi } from './api/useAuthApi';

type Client = { id: string | number; name: string; fulfillment_id?: string | number | null }

const LS_KEY = 'current_client_id'

function readStoredClientId(): string | null {
  if (!process.client) return null
  return window.localStorage.getItem(LS_KEY)
}

function syncCurrentClientWithList(
  list: Client[],
  currentClientId: ReturnType<typeof useState<string | number | null>>,
) {
  if (!list.length) {
    currentClientId.value = null
    return
  }

  const saved = currentClientId.value ?? readStoredClientId()
  const exists = saved != null && list.some(c => String(c.id) === String(saved))
  currentClientId.value = exists ? saved : (list[0]?.id ?? null)
}

export function useCurrentClient() {
  const clients = useState<Client[]>('clients', () => [])
  const currentClientId = useState<string | number | null>('currentClientId', () => readStoredClientId())
  const isReady = useState<boolean>('currentClientReady', () => false)

  const currentClient = computed<Client | null>(() =>
    clients.value.find(c => String(c.id) === String(currentClientId.value ?? '')) ?? null
  )

  async function loadClients() {
    const { data, error } = await useAuthApi<Client[]>('/api/client-users/clients', {
      method: 'GET'
    })
    if (!error.value && data.value) {
      clients.value = data.value
      syncCurrentClientWithList(data.value, currentClientId)
    }
  }

  function setClient(id: string | number | null) {
    const prev = currentClientId.value
    currentClientId.value = id

    if (id != null && String(id) !== String(prev ?? '')) {
      const { checkOnboarding } = useClientOnboarding()
      void checkOnboarding(Number(id))
    }
  }

  function updateClientInState(updated: Partial<Client> & { id: string | number }) {
    const targetId = String(updated.id)
    clients.value = clients.value.map(client =>
      String(client.id) === targetId ? { ...client, ...updated } : client
    )
  }

  function loadFromStorage() {
    const saved = readStoredClientId()
    if (saved) {
      currentClientId.value = saved
    }
  }

  // Автоматически сохраняем в localStorage при изменении
  if (process.client) {
    watch(
      currentClientId,
      val => {
        if (val != null) {
          window.localStorage.setItem(LS_KEY, String(val))
        }
      },
      { immediate: true }
    )
  }

  function clearClient() {
    currentClientId.value = null
    clients.value = []
    isReady.value = false
    initPromise = null
    const { clearAllOnboardingCache } = useClientOnboarding()
    clearAllOnboardingCache()
    if (process.client) {
      window.localStorage.removeItem(LS_KEY)
    }
  }

  // async function init() {
  //   if (isReady.value) return
  //   loadFromStorage()
  //   if (clients.value.length === 0) {
  //     await loadClients()
  //   }

  //   isReady.value = true
  // }

  let initPromise: Promise<void> | null = null

  async function init() {
    if (isReady.value) return
    if (initPromise) return initPromise

    initPromise = (async () => {
      loadFromStorage()
      if (clients.value.length === 0) {
        await loadClients()
      } else {
        syncCurrentClientWithList(clients.value, currentClientId)
      }
      isReady.value = true

      if (currentClientId.value != null) {
        const { checkOnboarding } = useClientOnboarding()
        await checkOnboarding(Number(currentClientId.value))
      }
    })()

    return initPromise
  }

  // onMounted(() => {
  //   void init()
  // })

  return {
    clients: computed(() => clients.value),
    currentClientId: computed(() => currentClientId.value),
    currentClient: computed(() => currentClient.value),
    isReady: computed(() => isReady.value),
    init,
    loadClients,
    setClient,
    updateClientInState,
    loadFromStorage,
    clearClient,
  }
}
